import React, { useMemo, useState, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { formatNumber, formatNumberCommas } from "@/shared/utils/formatters";
import { Icon } from "@/shared/components/Icon/Icon";
import { AppTooltip } from "@/shared/components/Tooltip/AppTooltip";
import { getMetricsByPlatform, MetricConfig } from "@/shared/lib/metricConfigs";
import { AnalysisVideo } from "@/api/campaign";
import { ChevronDown } from "lucide-react";
import { useMaxWidth } from "@/shared/hooks/useMaxWidth";
import { PHONE_MAX_WIDTH, mediaPhone } from "@/shared/theme/breakpoints";
import { pressedTint } from "@/shared/theme/pressed";
import { MetricsCarousel } from "./MetricsCarousel";

const MetricsContainer = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const MetricsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
	gap: 16px;
	width: 100%;
	position: relative;
`;

const MetricCard = styled.div`
	display: inline-flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	padding: 16px;
	background: white;
	box-shadow: 0px 4px 12px rgba(21, 89, 165, 0.04);
	border-radius: 16px;
	gap: 10px;
	width: 100%;
	height: 125px;
	position: relative;

	${mediaPhone`
		height: 100%;
		min-height: 108px;
		padding: 12px;
		gap: 8px;
		border-radius: 12px;
		cursor: pointer;
		container-type: inline-size;
		${pressedTint}
	`}
`;

const MetricHeader = styled.div`
	align-self: stretch;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	gap: 8px;

	${mediaPhone`
		gap: 6px;
	`}

	/* The narrowest phones (cards under 125px): icon above the label, so a long one-word label fits. */
	@container (max-width: 125px) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

const MetricIcon = styled.div`
	padding: 6px;
	background: #ebeefd;
	border-radius: 8px;
	display: flex;
	justify-content: center;
	align-items: center;
	flex-shrink: 0;
	width: 36px;
	height: 36px;

	${mediaPhone`
		width: 30px;
		height: 30px;
		padding: 5px;
	`}
`;

const MetricLabel = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	color: #303030;
	font-size: 14px;
	font-family: "Poppins", sans-serif;
	font-weight: 400;
	word-wrap: break-word;
	cursor: help;

	/* Two lines at most; the tap tooltip carries the full name and formula. */
	${mediaPhone`
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		overflow: hidden;
		overflow-wrap: normal;
		word-break: normal;
		flex: 1 1 auto;
		min-width: 0;
		font-size: clamp(11px, 3.4vw, 13px);
		line-height: 1.35;
	`}
`;

const MetricValueContainer = styled.div`
	align-self: stretch;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	gap: 10px;
`;

const MetricValue = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	color: #303030;
	font-size: 38px;
	font-family: "Poppins", sans-serif;
	font-weight: 500;
	word-wrap: break-word;

	${mediaPhone`
		font-size: clamp(20px, 6.5vw, 26px);
		line-height: 1.25;
		white-space: nowrap;
	`}
`;

const TooltipContent = styled.div`
	padding: 4px;
	max-width: 250px;
`;

const TooltipDesc = styled.div`
	font-size: 12px;
	color: #e0e0e0;
	font-weight: 500;
	margin-bottom: 8px;
	white-space: pre-line;
`;

const TooltipFormula = styled.div`
	font-size: 12px;
	border-radius: 4px;
	white-space: pre-line;
	flex-shrink: 1;
	min-width: 0;
`;

const ExpandButton = styled(motion.button)`
	width: 100%;
	height: 100%;
	padding: 10px;
	border-top: 1px solid #cdd4f6;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: 10px;
	display: inline-flex;
	background: transparent;
	border-bottom: none;
	border-left: none;
	border-right: none;
	cursor: pointer;
	transition: background 0.2s;

	&:hover {
		background: rgba(0, 0, 0, 0.02);
	}
`;

const ExpandButtonContent = styled.div`
	align-self: stretch;
	justify-content: center;
	align-items: center;
	gap: 4px;
	display: inline-flex;
`;

const ChevronIcon = styled.div<{ $isExpanded: boolean }>`
	width: 24px;
	height: 24px;
	position: relative;
	overflow: hidden;
	transition: transform 0.2s;
	transform: ${(props) => (props.$isExpanded ? "rotate(180deg)" : "rotate(0deg)")};

	&::after {
		content: "";
		width: 12px;
		height: 6px;
		left: 6px;
		top: 9px;
		position: absolute;
	}
`;

const ExpandButtonText = styled.div`
	text-align: center;
	color: #303030;
	font-size: 14px;
	font-family: "Poppins", sans-serif;
	font-weight: 500;
	line-height: 24px;
	word-wrap: break-word;
`;

interface PerformanceMetricsProps {
	platform: string;
	split: string;
	videos: AnalysisVideo[];
}

export const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({ platform, split, videos }) => {
	const isPhone = useMaxWidth(PHONE_MAX_WIDTH);
	const [isExpanded, setIsExpanded] = useState(false);
	const gridRef = useRef<HTMLDivElement>(null);
	const [columns, setColumns] = useState(0);
	const isPerCreatorSplit = split === "perCreator";

	// Calculate columns based on container width and minmax(250px)
	React.useEffect(() => {
		const updateColumns = () => {
			if (gridRef.current) {
				const width = gridRef.current.offsetWidth;
				const gap = 16;
				const minWidth = 250;
				const count = Math.floor((width + gap) / (minWidth + gap));
				setColumns(Math.max(1, count));
			}
		};

		updateColumns();
		window.addEventListener("resize", updateColumns);
		return () => window.removeEventListener("resize", updateColumns);
	}, []);

	const metricConfigs = useMemo(() => {
		const useSplit = split && split.startsWith("creator:") ? ("perCreator" as any) : (split as any);
		return getMetricsByPlatform(platform, useSplit as any);
	}, [platform, split]);

	const videosToUse = useMemo(() => {
		if (!split || !split.startsWith("creator:")) return videos;
		const creatorId = split.split(":")[1];
		return videos.filter((v) => {
			const id =
				(v as any).author?.handleName || (v as any).creatorId || (v as any).uploaderId || (v as any).authorId;
			return id === creatorId;
		});
	}, [videos, split]);

	const creatorVideoGroups = useMemo(() => {
		if (!isPerCreatorSplit) return [];

		const groups = new Map<string, AnalysisVideo[]>();

		videosToUse.forEach((video) => {
			const creatorId =
				(video as any).author?.handleName ||
				(video as any).creatorId ||
				(video as any).uploaderId ||
				(video as any).authorId ||
				"unknown";
			const existing = groups.get(creatorId) || [];
			existing.push(video);
			groups.set(creatorId, existing);
		});

		return Array.from(groups.values());
	}, [isPerCreatorSplit, videosToUse]);

	const calculatedMetrics = useMemo(() => {
		const vids = videosToUse;
		if (!vids || vids.length === 0) return {};

		const results: Record<string, string | number> = {};

		const parseStr = (val: any): number => {
			if (typeof val === "number") return val;
			if (typeof val === "string") return parseFloat(val.replace(/[^0-9.]/g, "")) || 0;
			return 0;
		};

		const getNum = (v: AnalysisVideo, key: string): number => {
			if (key === "engagements" || key === "organicEngagements" || key === "paidEngagements") {
				const prefix = key.startsWith("organic") ? "organic" : key.startsWith("paid") ? "paid" : "";
				const l = parseStr((v as any)[prefix ? `${prefix}Likes` : "likes"]);
				const c = parseStr((v as any)[prefix ? `${prefix}Comments` : "comments"]);
				const s = parseStr((v as any)[prefix ? `${prefix}Shares` : "shares"]);
				const sv = parseStr((v as any)[prefix ? `${prefix}Saves` : "saves"]);
				return l + c + s + sv;
			}

			let dataKey = key;
			if (key === "views") dataKey = "viewCount";
			if (key === "avgEngagementRate") dataKey = "engagementRate";
			if (key === "avgWatchTime") dataKey = "averageWatchTime";
			if (key === "avgRetentionRate") dataKey = "retentionRate";

			return parseStr((v as any)[dataKey]);
		};

		const calculateMetricValue = (vidList: AnalysisVideo[], metricId: string): string | number => {
			let value: string | number = 0;

			switch (metricId) {
				case "views":
				case "organicViews":
				case "paidViews":
				case "likes":
				case "organicLikes":
				case "paidLikes":
				case "comments":
				case "organicComments":
				case "paidComments":
				case "shares":
				case "organicShares":
				case "paidShares":
				case "saves":
				case "organicSaves":
				case "paidSaves":
				case "reposts":
				case "engagements":
				case "organicEngagements":
				case "paidEngagements":
				case "impressions":
				case "linkClicks":
					value = vidList.reduce((acc, v) => acc + getNum(v, metricId), 0);
					break;

				case "avgEngagementRate":
				case "organicEngagementRate":
				case "paidEngagementRate": {
					const totalViews = vidList.reduce((acc, v) => {
						const prefix = metricId.startsWith("organic")
							? "organic"
							: metricId.startsWith("paid")
								? "paid"
								: "";
						return acc + getNum(v, prefix ? `${prefix}Views` : "views");
					}, 0);
					const totalEng = vidList.reduce((acc, v) => {
						const prefix = metricId.startsWith("organic")
							? "organic"
							: metricId.startsWith("paid")
								? "paid"
								: "";
						return acc + getNum(v, prefix ? `${prefix}Engagements` : "engagements");
					}, 0);
					value = totalViews > 0 ? ((totalEng / totalViews) * 100).toFixed(2) + "%" : "0%";
					break;
				}

				case "avgWatchTime": {
					const wt = vidList.map((v) => getNum(v, metricId)).filter((v) => v > 0);
					value = wt.length ? formatTime(wt.reduce((a, b) => a + b, 0) / wt.length) : "0s";
					break;
				}

				case "followerReach": {
					const eligibleVids = vidList.filter((v) => parseStr(v.followerCount) > 0);
					const totalViews = eligibleVids.reduce((acc, v) => acc + getNum(v, "views"), 0);
					const totalFollowers = eligibleVids.reduce((acc, v) => acc + parseStr(v.followerCount), 0);
					value = totalFollowers > 0 ? ((totalViews / totalFollowers) * 100).toFixed(2) + "%" : "0%";
					break;
				}

				case "followerRelevance": {
					const eligibleVids = vidList.filter((v) => parseStr(v.followerCount) > 0);
					const totalEng = eligibleVids.reduce((acc, v) => acc + getNum(v, "engagements"), 0);
					const totalFollowers = eligibleVids.reduce((acc, v) => acc + parseStr(v.followerCount), 0);
					value = totalFollowers > 0 ? ((totalEng / totalFollowers) * 100).toFixed(2) + "%" : "0%";
					break;
				}

				case "saveToViewRatio": {
					const totalSaves = vidList.reduce((acc, v) => acc + getNum(v, "saves"), 0);
					const totalViews = vidList.reduce((acc, v) => acc + getNum(v, "views"), 0);
					value = totalViews > 0 ? ((totalSaves / totalViews) * 100).toFixed(2) + "%" : "0%";
					break;
				}

				case "shareToViewRatio": {
					const totalShares = vidList.reduce((acc, v) => acc + getNum(v, "shares"), 0);
					const totalViews = vidList.reduce((acc, v) => acc + getNum(v, "views"), 0);
					value = totalViews > 0 ? ((totalShares / totalViews) * 100).toFixed(2) + "%" : "0%";
					break;
				}

				case "cpv": {
					const totalViews = vidList.reduce((acc, v) => acc + getNum(v, "views"), 0);
					// Group by creator to get unique rates
					const creatorRates = new Map<string, number>();
					vidList.forEach((v) => {
						const creatorId = (v as any).author?.handleName || (v as any).creatorId;
						if (creatorId && v.finalRate !== undefined && v.finalRate !== null) {
							creatorRates.set(creatorId, Number(v.finalRate));
						}
					});
					const totalCost = Array.from(creatorRates.values()).reduce((sum, rate) => sum + rate, 0);
					value = totalViews > 0 ? "$" + ((totalCost / totalViews) * 1000).toFixed(2) : "$0.00";
					break;
				}

				case "cpe": {
					const totalEng = vidList.reduce((acc, v) => acc + getNum(v, "engagements"), 0);
					const creatorRates = new Map<string, number>();
					vidList.forEach((v) => {
						const creatorId = (v as any).author?.handleName || (v as any).creatorId;
						if (creatorId && v.finalRate !== undefined && v.finalRate !== null) {
							creatorRates.set(creatorId, Number(v.finalRate));
						}
					});
					const totalCost = Array.from(creatorRates.values()).reduce((sum, rate) => sum + rate, 0);
					value = totalEng > 0 ? "$" + ((totalCost / totalEng) * 1000).toFixed(2) : "$0.00";
					break;
				}

				default: {
					const vals = vidList.map((v) => getNum(v, metricId)).filter((v) => v > 0);
					if (vals.length > 0) {
						const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
						value = avg;
					} else {
						value = 0;
					}
				}
			}

			return value;
		};

		if (isPerCreatorSplit && creatorVideoGroups.length > 0) {
			metricConfigs.forEach((config: MetricConfig) => {
				const perCreatorValues = creatorVideoGroups.map((group) => calculateMetricValue(group, config.id));

				if (perCreatorValues.length === 0) {
					results[config.id] = config.isPercentage ? "0%" : 0;
					return;
				}

				if (typeof perCreatorValues[0] === "number") {
					const numericValues: number[] = perCreatorValues.map((value) =>
						typeof value === "number" ? value : parseStr(value),
					);
					const average = numericValues.reduce((acc, value) => acc + value, 0) / numericValues.length;
					results[config.id] = config.isPercentage ? `${average.toFixed(2)}%` : average;
					return;
				}

				const numericValues: number[] = perCreatorValues.map((value) => parseStr(value));
				const average = numericValues.reduce((acc, value) => acc + value, 0) / numericValues.length;
				results[config.id] = config.isPercentage ? `${average.toFixed(2)}%` : average;
			});

			return results;
		}

		metricConfigs.forEach((config: MetricConfig) => {
			results[config.id] = calculateMetricValue(vids, config.id);
		});

		return results;
	}, [creatorVideoGroups, isPerCreatorSplit, metricConfigs, videosToUse]);

	const visibleConfigs = metricConfigs.filter((config: MetricConfig) => config.visible);
	const initialVisibleCount = columns * 2 || 10; // Fallback to 10 if columns not yet calculated

	// Phones page through every metric in the carousel, so nothing is held back behind "Show more".
	const renderTile = (config: MetricConfig) => {
		const rawValue = calculatedMetrics[config.id];
		const displayValue =
			typeof rawValue === "number"
				? isPhone
					? formatNumber(rawValue)
					: formatNumberCommas(rawValue)
				: rawValue || "0";

		return (
			<AppTooltip
				key={config.id}
				maxWidth={330}
				trigger={isPhone ? "click" : "hover"}
				title={
					<TooltipContent>
						<TooltipDesc>{config.description}</TooltipDesc>
						<div style={{ display: "flex", gap: 10 }}>
							<div>
								<Icon src="/icon/menu_dashboard.svg" color="#ffffff" size={18} />
							</div>
							<TooltipFormula>{config.formula}</TooltipFormula>
						</div>
					</TooltipContent>
				}
				placement="top"
			>
				<MetricCard>
					<MetricHeader>
						<MetricIcon>
							<Icon src={config.icon} size={20} color="#3F37C9" />
						</MetricIcon>

						<MetricLabel>{config.label}</MetricLabel>
					</MetricHeader>
					<MetricValueContainer>
						<MetricValue>{displayValue}</MetricValue>
					</MetricValueContainer>
				</MetricCard>
			</AppTooltip>
		);
	};

	if (isPhone) {
		return (
			<MetricsContainer>
				<MetricsCarousel tiles={visibleConfigs.map(renderTile)} />
			</MetricsContainer>
		);
	}

	return (
		<MetricsContainer>
			<MetricsGrid ref={gridRef}>
				{visibleConfigs.map((config: MetricConfig, index: number) =>
					index >= initialVisibleCount && !isExpanded ? null : renderTile(config),
				)}
			</MetricsGrid>

			<AnimatePresence>
				{visibleConfigs.length > 10 && (
					<ExpandButton
						layout
						transition={{ duration: 0.15, ease: "easeOut" }}
						onClick={() => setIsExpanded(!isExpanded)}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					>
						<ExpandButtonContent>
							<ChevronIcon $isExpanded={isExpanded}>
								<ChevronDown />
							</ChevronIcon>
							<div style={{ justifyContent: "center", alignItems: "center", gap: 10, display: "flex" }}>
								<ExpandButtonText>{isExpanded ? "Show less" : "Show more"}</ExpandButtonText>
							</div>
						</ExpandButtonContent>
					</ExpandButton>
				)}
			</AnimatePresence>
		</MetricsContainer>
	);
};

const formatTime = (seconds: number): string => {
	if (!seconds) return "0s";
	const mins = Math.floor(seconds / 60);
	const secs = Math.round(seconds % 60);
	return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};
